/**
 * SignUpForm — Phase E split (May 6, 2026).
 *
 * Presentational form. State is owned by the parent (`AuthDialog`).
 * Markup, classNames, data-testids, and submit semantics are
 * byte-identical to the previous monolithic `AuthModals` component.
 */
import { Mail, Lock, User, AlertCircle } from 'lucide-react';
import { Button } from '@/ui/button';
import { Input } from '@/ui/input';
import { Label } from '@/ui/label';
import { DialogBody, DialogFooter } from '@/ui/dialog';

const SignUpForm = ({
  email,
  password,
  confirmPassword,
  displayName,
  showPassword,
  authError,
  authLoading,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onDisplayNameChange,
  onSubmit,
  onSwitchToSignIn,
}) => (
  <form onSubmit={onSubmit} className="flex flex-col flex-1 min-h-0">
    <DialogBody className="space-y-4">
      {authError && (
        <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{authError}</span>
        </div>
      )}

      <div>
        <Label className="text-slate-300">Email</Label>
        <div className="relative mt-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="your@email.com"
            className="pl-10 bg-slate-900 border-slate-700 text-white"
            data-testid="signup-email-input"
          />
        </div>
      </div>
      <div>
        <Label className="text-slate-300">Password</Label>
        <div className="relative mt-1">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder="••••••••"
            className="pl-10 bg-slate-900 border-slate-700 text-white"
            data-testid="signup-password-input"
          />
        </div>
      </div>
      <div>
        <Label className="text-slate-300">Repeat Password</Label>
        <div className="relative mt-1">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => onConfirmPasswordChange(e.target.value)}
            placeholder="••••••••"
            className="pl-10 bg-slate-900 border-slate-700 text-white"
            data-testid="signup-confirm-password-input"
          />
        </div>
      </div>
      <div>
        <Label className="text-slate-300">Display Name</Label>
        <div className="relative mt-1">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            type="text"
            value={displayName}
            onChange={(e) => onDisplayNameChange(e.target.value)}
            placeholder="Your Name"
            className="pl-10 bg-slate-900 border-slate-700 text-white"
            data-testid="signup-displayname-input"
          />
        </div>
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={onSwitchToSignIn}
          className="text-sm text-teal-400 hover:text-teal-300"
          data-testid="signin-existing-link"
        >
          Sign Into Existing Account
        </button>
      </div>
    </DialogBody>

    <DialogFooter className="border-slate-700 bg-slate-800">
      <Button
        type="submit"
        disabled={authLoading}
        className="w-full bg-teal-500 hover:bg-teal-400 text-slate-900"
        data-testid="signup-submit-btn"
      >
        {authLoading ? 'Creating Account...' : 'Submit Form And Register'}
      </Button>
    </DialogFooter>
  </form>
);

export default SignUpForm;
